import React, { ReactNode, useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthentication } from "@shared/contexts/AuthenticationContext";
import { PagePaths } from "@shared/constants";
import { ComposeURLWithGetQueryArguments } from "@shared/utils";
import { OAuth2QueryParameters } from "@features/authentication/constants/Oauth2";
import { verifyCode } from "@shared/contexts/render/RenderOnBoarding";
import ErrorPage from "@pages/ErrorPage";
import { TokenExpirationChecker } from "@shared/utils/token/TokenExpirationChecker";

type RenderAuthenticationProps = { children: ReactNode };

const STEP_PAGE_MAPPING: { [key: number]: string } = {
  1: PagePaths.LOGIN.IDENTITY,
  2: PagePaths.LOGIN.PASSWORD,
  3: PagePaths.LOGIN.VERIFYEMAIL,
  4: PagePaths.LOGIN.SENDRECOVERYPASSWORDLINK,
  5: PagePaths.LOGIN.RECOVERYPASSWORD,
  6: PagePaths.LOGIN.SECONDFACTOR,
};

export const RenderAuthentication = ({ children }: RenderAuthenticationProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authData, setAuthData } = useAuthentication();

  const [isValidCode, setIsValidCode] = useState(true);

  const handleRecoveryPasswordPage = useCallback(async (currentPage: string) => {
    const queryParams = new URLSearchParams(location.search);
    const code = queryParams.get(OAuth2QueryParameters.CODE) || "";
    const isValidCodeResult = await verifyCode(code);
    if (!isValidCodeResult) setIsValidCode(false);
    return 5;
  }, [location.search]);

  const handleBackwardNavigation = useCallback((currentPage: string, currentStep: number) => {
    const currentStepNumber = Object.keys(STEP_PAGE_MAPPING).find(
      key => STEP_PAGE_MAPPING[parseInt(key)] === currentPage
    );
    
    if (currentStepNumber && parseInt(currentStepNumber) < currentStep) {
      if (process.env.NODE_ENV === 'development') {
        console.log("Redirecting backwards from", currentPage, "to step", currentStep);
      }
      navigate(ComposeURLWithGetQueryArguments(STEP_PAGE_MAPPING[currentStep]));
    }
  }, [navigate]);

  const redirectToStep = useCallback((path: string, params?: Record<string, string>) => {
    navigate(ComposeURLWithGetQueryArguments(path, params));
  }, [navigate]);

  const isIdentityTokenExpired = useCallback((currentPage: string): boolean => {
    return currentPage === PagePaths.LOGIN.PASSWORD &&
      !!authData.identityRequestResponse &&
      TokenExpirationChecker.isTokenExpired(authData.identityRequestResponse, 30);
  }, [authData.identityRequestResponse]);

  const isMissingIdentityToken = useCallback((currentStep: number): boolean =>
    currentStep >= 2 && !authData.identityRequestResponse,
  [authData.identityRequestResponse]);

  const isMissingPasswordToken = useCallback((currentStep: number): boolean =>
    currentStep >= 3 && !authData.passwordRequestResponse,
  [authData.passwordRequestResponse]);

  const validateTokenConsistency = useCallback((currentStep: number, currentPage: string) => {
    if (isMissingIdentityToken(currentStep)) {
      console.warn("Missing identity token, redirecting to identity step");
      redirectToStep(PagePaths.LOGIN.IDENTITY);
      return false;
    }
    
    if (isIdentityTokenExpired(currentPage)) {
      console.warn("Identity token expired, redirecting to identity step with error message");
      setAuthData({ ...authData, step: 1, identityRequestResponse: undefined });
      redirectToStep(PagePaths.LOGIN.IDENTITY, { tokenExpired: "true" });
      return false;
    }
    
    if (isMissingPasswordToken(currentStep)) {
      console.warn("Missing password token, redirecting to password step");
      redirectToStep(PagePaths.LOGIN.PASSWORD);
      return false;
    }
    
    return true;
  }, [authData, redirectToStep, setAuthData, isIdentityTokenExpired, isMissingIdentityToken, isMissingPasswordToken]);

  const defineRender = useCallback(async () => {
    const currentPage = location.pathname;
    const isLoginPage = currentPage.startsWith("/login/");
    if (!isLoginPage) return;

    if (currentPage === PagePaths.LOGIN.VERIFYEMAIL) return;

    let currentStep = authData.step ?? 1;
    const isRecoveryPage = currentPage === PagePaths.LOGIN.RECOVERYPASSWORD;
    const isWrongStepPage = currentPage !== STEP_PAGE_MAPPING[currentStep];

    if (isRecoveryPage) {
      currentStep = await handleRecoveryPasswordPage(currentPage);
    } else if (isWrongStepPage) {
      handleBackwardNavigation(currentPage, currentStep);
    }

    validateTokenConsistency(currentStep, currentPage);
  }, [authData.step, location.pathname, handleRecoveryPasswordPage, handleBackwardNavigation, validateTokenConsistency]); 
  
  useEffect(() => {
    defineRender();
  }, [authData.step, authData.username, navigate, location.pathname, defineRender]);

  if (!isValidCode) return <ErrorPage errorType="oauth" missingArguments={["code"]} />; 

  return <>{children}</>;
};
